/* import __COLOCATED_TEMPLATE__ from './paginated-activity-logs.hbs'; */
/* RESPONSIBLE TEAM: team-app-security */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import moment, { type Moment } from 'moment-timezone';
import { inject as service } from '@ember/service';
import ajax from 'embercom/lib/ajax';
import { ADMIN_EVENT_TYPES } from 'embercom/lib/admins/app-admin-events-constants';
import type Store from '@ember-data/store';
import type AppAdminEvent from 'embercom/models/app-admin-event';
import Admin from 'embercom/models/admin';
import { htmlToTextContent } from 'embercom/lib/html-unescape';
import { DATE_WITHOUT_TIME, CSV_EMPTY_LINE } from 'embercom/lib/reporting/export-helper';
import ExportHelper from 'embercom/lib/reporting/export-helper';
import AppAdminEventsHelper from 'embercom/lib/admins/app-admin-events-helper';
import { getOwner } from '@ember/application';
import Range from 'embercom/models/reporting/range';

interface FetchParams {
  from: number;
  to: number;
  page: number;
  per_page: number;
  admin_id?: number;
  event_type?: string;
}

interface LogsResponse {
  events: AppAdminEvent[];
  page: number;
  per_page: number;
  total_pages: number;
  next: string | null;
  total_count: number;
}

export default class ActivityLogs extends Component {
  @service appService: any;
  @service declare notificationsService: any;
  @service intl: any;
  @service store!: Store;
  @service csv: any;
  @tracked events: AppAdminEvent[] = [];
  @tracked csvEvents: AppAdminEvent[] = [];
  @tracked isLoading = false;
  @tracked currentPage = 1;
  @tracked totalCount = 0;
  @tracked perPage = 25;
  @tracked totalPages = 1;
  @tracked fromDate: Date;
  @tracked toDate: Date;
  @tracked nextPageUrl: string | null = null;
  @tracked filterByAdmin: any = null;
  @tracked filterByEventType = 'AppAdminEvents::All';
  @tracked isError = false;
  @tracked maxDays = 365;
  @tracked isDateTooOld = false;
  @tracked exportFailed = false;

  private eventsHelper: AppAdminEventsHelper;
  private exportHelper: ExportHelper;

  constructor(owner: unknown, args: any) {
    super(owner, args);
    this.fromDate = moment().subtract(7, 'days').toDate();
    this.toDate = new Date();
    this.eventsHelper = new AppAdminEventsHelper(getOwner(this));
    this.exportHelper = new ExportHelper(getOwner(this));
    this.loadEvents();
  }

  private async fetchEvents(url: string): Promise<void> {
    this.isLoading = true;
    this.isError = false;
    this.isDateTooOld = false;

    try {
      let response: LogsResponse = await ajax({ url, type: 'GET' });
      this.store.pushPayload({ 'app-admin-events': response.events });
      this.events = this.populateAdminData(response);
      this.currentPage = response.page;
      this.perPage = response.per_page;
      this.totalPages = response.total_pages;
      this.totalCount = response.total_count;
      this.nextPageUrl = response.next;
    } catch (error) {
      console.error('Error loading app admin events:', error);
      let errorMessage = error.jqXHR?.responseJSON?.errors?.[0]?.message;
      // eslint-disable-next-line @intercom/intercom/no-bare-strings
      if (errorMessage?.includes('from timestamp cannot be more than 1 year ago')) {
        this.isDateTooOld = true;
      } else {
        this.isError = true;
      }
    } finally {
      this.isLoading = false;
    }
  }

  private async fetchCsvEvents(url: string): Promise<void> {
    try {
      let response = await ajax({ url, type: 'GET' });
      this.store.pushPayload({ 'app-admin-events': response.events });
      this.csvEvents = this.populateAdminData(response);
    } catch (error) {
      this.exportFailed = true;
    }
  }

  private async loadEvents(): Promise<void> {
    let params = this.buildParams();
    let url = this.buildUrlWithParams(params);
    await this.fetchEvents(url);
  }

  private populateAdminData(response: LogsResponse): AppAdminEvent[] {
    return response.events.map((eventData) => {
      let event = this.store.peekRecord('app-admin-event', eventData.id);
      // admin_id can be null or 0
      if (!event.admin && eventData.admin_id) {
        event.admin = Admin.peekAndMaybeLoad(this.store, eventData.admin_id.toString());
      }
      return event;
    });
  }

  private buildParams(): FetchParams {
    let params: FetchParams = {
      from: moment(this.fromDate).tz(this.app.timezone).unix(),
      to: moment(this.toDate).tz(this.app.timezone).unix(),
      page: this.currentPage,
      per_page: this.perPage,
    };

    if (this.filterByAdmin) {
      params.admin_id = this.filterByAdmin.id;
    }

    if (this.filterByEventType && this.filterByEventType !== 'AppAdminEvents::All') {
      params.event_type = this.filterByEventType;
    }

    return params;
  }

  private buildUrlWithParams(params: FetchParams): string {
    let query = new URLSearchParams(params as any).toString();
    return `/ember/app/${this.app.id}/admin-events.json?${query}`;
  }

  private buildCsvUrlWithParams(params: FetchParams): string {
    let query = new URLSearchParams(params as any).toString();
    return `/ember/app/${this.app.id}/admin-events/csv?${query}`;
  }

  private csvData() {
    return this.csvEvents.map((event: AppAdminEvent) => {
      let description = htmlToTextContent(this.eventsHelper.descriptionHTML(event));
      let activityType = this.intl.t(
        ADMIN_EVENT_TYPES.findBy('value', event.type)?.text ??
          'settings.content-components.logs.unknown-activity',
      );
      let date = this.intl.formatDate(event.created_at, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        timeZone: this.app.timezone,
        timeZoneName: 'short',
      });
      return [activityType, event.admin?.name || '', description, date, event.admin_ip];
    });
  }

  private csvFilters() {
    return [
      [this.intl.t('settings.content-components.logs.filters')],
      [
        this.intl.t('settings.content-components.logs.teammate'),
        this.intl.t('settings.content-components.logs.activity'),
      ],
      [
        this.filterByAdmin?.name || this.intl.t('settings.content-components.logs.all'),
        this.intl.t(ADMIN_EVENT_TYPES.findBy('value', this.filterByEventType)?.text || ''),
      ],
    ];
  }

  private headerRow() {
    return [
      [
        this.intl.t('settings.content-components.logs.activity'),
        this.intl.t('settings.content-components.logs.teammate'),
        this.intl.t('settings.content-components.logs.description'),
        this.intl.t('settings.content-components.logs.date'),
        this.intl.t('settings.content-components.logs.ip-address'),
      ],
    ];
  }

  private filenameFormattedDate(date: Date) {
    return this.exportHelper.dateFormatter(moment(date), DATE_WITHOUT_TIME);
  }

  private formatDate(date: string | Moment) {
    // eslint-disable-next-line @intercom/intercom/no-bare-strings
    return moment(date).tz(this.app.timezone).format('MMM DD, YYYY, h:mm A z');
  }

  private buildEventType(eventType: any) {
    return {
      text: this.intl.t(eventType.text),
      value: eventType.value,
      isSelected: this.filterByEventType === eventType.value,
    };
  }

  get adminEventTypes() {
    // Ensure "All Activity" is always the first item
    let allOption = this.buildEventType({
      text: 'settings.content-components.logs.all-activity',
      value: 'AppAdminEvents::All',
    });

    let options = ADMIN_EVENT_TYPES.map((type) => this.buildEventType(type));

    return [
      {
        hasItemsMarkableAsSelected: true,
        items: [
          allOption,
          ...options
            .filter((type) => type.value !== 'AppAdminEvents::All')
            .filter((type) =>
              this.app.canUseStandalone ? type.value !== 'AppAdminEvents::SeatChange' : true,
            )
            .filter(
              (type) =>
                this.app.canUseManualRedaction ||
                type.value !== 'AppAdminEvents::ConversationPartRedaction',
            )
            .filter(
              (type) =>
                this.app.canUseEditableSlaDefinitions ||
                type.value !== 'AppAdminEvents::ConversationSlaChange',
            )
            .sort((a, b) => a.text.localeCompare(b.text)),
        ],
      },
    ];
  }

  get tableData() {
    let events = this.filterEvents(this.events);
    return events.map((event) => ({
      event,
      activityType: this.getActivityType(event.type),
      teammateName: event.admin?.name || '',
      teammateId: event.admin?.id,
      date: this.formatDate(event.created_at),
      ipAddress: event.admin_ip || '',
    }));
  }

  private filterEvents(events: AppAdminEvent[]) {
    if (!this.app.canUseStandalone) {
      return events;
    }
    return events.filter((event) => event.type !== 'AppAdminEvents::SeatChange');
  }

  private getActivityType(activityType: string) {
    return ADMIN_EVENT_TYPES.find((type) => type.value === activityType)?.text || activityType;
  }

  get app() {
    return this.appService.app;
  }

  @action
  async nextPage(): Promise<void> {
    if (this.nextPageUrl) {
      await this.fetchEvents(this.nextPageUrl);
    }
  }

  @action
  async previousPage(): Promise<void> {
    if (this.currentPage > 1) {
      this.currentPage--;
      await this.loadEvents();
    }
  }

  @action
  setFromDate(date: Date) {
    let newFromDate = moment(date).tz(this.app.timezone);
    let currentToDate = moment(this.toDate).tz(this.app.timezone);

    // If the new from date is after the current to date, adjust the to date
    if (newFromDate.isAfter(currentToDate)) {
      this.toDate = newFromDate.toDate();
    }

    this.fromDate = newFromDate.toDate();
    this.applyFilters();
  }

  @action
  setToDate(date: Date) {
    let newToDate = moment(date).tz(this.app.timezone);
    let currentFromDate = moment(this.fromDate).tz(this.app.timezone);

    if (newToDate.isBefore(currentFromDate)) {
      this.fromDate = newToDate.toDate();
    }

    this.toDate = newToDate.toDate();
    this.applyFilters();
  }

  @action
  async applyFilters(): Promise<void> {
    this.currentPage = 1;
    await this.loadEvents();
  }

  @action
  setFilterByAdmin(adminId: number) {
    let admin = this.app.admins.find((admin: any) => admin.id === adminId);
    this.filterByAdmin = admin;
    this.applyFilters();
  }

  @action
  setFilterByEventType(eventType: string) {
    this.filterByEventType = eventType;
    this.applyFilters();
  }

  @action
  async exportCsv() {
    let params = this.buildParams();
    let url = this.buildCsvUrlWithParams(params);
    await this.fetchCsvEvents(url);
    if (this.exportFailed === true) {
      this.notificationsService.notifyError(
        this.intl.t('settings.content-components.logs.export-error'),
      );
      return;
    }

    let filename = `teammate_activity_logs_${this.filenameFormattedDate(this.fromDate)}_${this.filenameFormattedDate(this.toDate)}.csv`;
    let title = [[this.intl.t('apps.app.settings.logs.title-token')]];
    let range = Range.createFromParams(this.fromDate, this.toDate, this.app.timezone);
    let data = title
      .concat(CSV_EMPTY_LINE)
      .concat(this.exportHelper.csvHeaders(range))
      .concat(CSV_EMPTY_LINE)
      .concat(this.csvFilters())
      .concat(CSV_EMPTY_LINE)
      .concat(this.headerRow())
      .concat(this.csvData());
    return await this.csv.export(data, {
      fileName: filename,
      withSeparator: false,
    });
  }

  @action
  async goToStart(): Promise<void> {
    if (this.currentPage !== 1) {
      this.currentPage = 1;
      await this.loadEvents();
    }
  }

  @action
  async goToEnd(): Promise<void> {
    if (this.currentPage !== this.totalPages) {
      this.currentPage = this.totalPages;
      await this.loadEvents();
    }
  }

  get toDateMinimum() {
    return this.fromDate;
  }

  get fromDateMaximum() {
    return this.toDate;
  }

  @action
  async retryLoad(): Promise<void> {
    await this.loadEvents();
  }

  get isExportDisabled() {
    return this.totalCount > 50000;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'NewSettings::Workspace::Teammates::PaginatedActivityLogs': typeof ActivityLogs;
    'new-settings/workspace/teammates/paginated-activity-logs': typeof ActivityLogs;
  }
}
